import React, { useMemo } from 'react';

import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import { EDITING } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import QButton from 'components/QButton';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  button: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    margin: `${theme.spacing(0.5)} 0`,
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    height: 40,
    textTransform: 'none',
    borderRadius: 20,
  },
  cancelButton: {
    margin: `${theme.spacing(0.5)} 0`,
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    height: 40,
    borderRadius: 20,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  errorTooltip: {
    background: 'transparent',
  },
  errorTooltipContent: {
    fontSize: theme.typography.paragraphRegular.fontSize,
    fontWeight: theme.typography.paragraphRegular.fontWeight,
    background: theme.palette.error.light,
    color: theme.palette.error.dark,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius * 3,
    '> svg': {
      height: 16,
      width: 16,
      marginRight: theme.spacing(0.5),
    },
  },
}));

const RuleFooter: React.FC<{ onSubmit: () => void; onCancel: () => void; onDelete: () => void }> = ({
  onSubmit,
  onCancel,
  onDelete,
}): JSX.Element => {
  const { classes } = useStyles();
  const { state } = useStateContext();
  const { stage, buttonText, buttonHelperText, error, rule } = state;

  const buttonEndIcon = stage === EDITING ? <ArrowForwardIcon /> : undefined;

  const createMarkup = useMemo(() => {
    return {
      __html: buttonHelperText.includes('existing & future')
        ? buttonHelperText.replace(
            /existing & future/g,
            '<span style="font-weight: 500; font-size: 14px;">existing & future</span>',
          )
        : buttonHelperText.includes('future')
          ? buttonHelperText.replace(/future/g, '<span style="font-weight: 500; font-size: 14px;">future</span>')
          : buttonHelperText,
    };
  }, [buttonHelperText]);

  return (
    <Box className={classes.row}>
      <Box className={classes.leftButtons}>
        {rule?.id && stage === EDITING && (
          <QButton id='rule-form-edit-button' variant={undefined} color='error' onClick={onDelete}>
            Delete Rule
          </QButton>
        )}
      </Box>

      <Box className={classes.rightButtons}>
        <Typography variant='paragraphRegular'>
          <span dangerouslySetInnerHTML={createMarkup} />
        </Typography>

        <QButton id='rule-form-cancel-button' variant={undefined} className={classes.cancelButton} onClick={onCancel}>
          Cancel
        </QButton>

        <Tooltip
          classes={{ tooltip: classes.errorTooltip }}
          title={
            <Box className={classes.errorTooltipContent}>
              <ErrorIcon />
              <Typography variant='paragraphRegular' component='span'>
                {error}
              </Typography>
            </Box>
          }
          open={!!error}
          arrow
        >
          <QButton
            id='rule-form-submit-button'
            className={classes.button}
            variant={'contained'}
            onClick={onSubmit}
            endIcon={buttonEndIcon}
          >
            {buttonText}
          </QButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default RuleFooter;
